import { t, Trans } from "@lingui/macro";
import { ETH_MAINNET } from "config/chains";
import { helperToast } from "lib/helperToast";
import { shortenAddress, useENS } from "lib/legacy";
import { useJsonRpcProvider } from "lib/rpc";
import { useCopyToClipboard } from "react-use";
import "./AddressDropdown.css";
import MetamaskIcon from "./images/metamaskIcon.svg";
import classes from "./AddressDropdownStyles.module.scss";
import { useEffect, useRef, useState } from "react";
import { ReactComponent as ShevronIcon } from "./images/shevron.svg";
import { ReactComponent as CopyIcon } from "./images/copyButton.svg";
import { ReactComponent as ExplorerIcon } from "./images/explorer.svg";
import { ReactComponent as DisconnectIcon } from "./images/disconnect.svg";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import classNames from "classnames";

type Props = {
  account: string;
  accountUrl: string;
  disconnectAccountAndCloseSettings: () => void;
};

function AddressDropdown({ account, accountUrl, disconnectAccountAndCloseSettings }: Props) {
  const [menuOpened, setMenuOpened] = useState(false);
  const [, copyToClipboard] = useCopyToClipboard();
  const { ensName } = useENS(account);
  const { provider: ethereumProvider } = useJsonRpcProvider(ETH_MAINNET);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !event.target.classList.contains("menu-button")
      ) {
        setMenuOpened(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  return (
    <>
      <div className={classes.component}>
        <button
          data-menu-button={true}
          onClick={() => setMenuOpened((prevState) => !prevState)}
          className={classNames(classes.root, "menu-button")}
        >
          {ethereumProvider && <img className={"menu-button"} width={16} height={16} src={MetamaskIcon} alt="" />}
          <span className={classNames(classes.address, "menu-button")}>{ensName || shortenAddress(account, 13)}</span>
          <ShevronIcon />
        </button>
        {menuOpened && (
          <motion.div
            className={classes.drawerMenu}
            key="menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            ref={dropdownRef}
          >
            <div
              onClick={() => {
                copyToClipboard(account);
                helperToast.success(t`Address copied to your clipboard`);
              }}
              className={classes.menuItemWrapper}
            >
              <CopyIcon />
              <span className={classes.menuItemText}>
                <Trans>Copy Address</Trans>
              </span>
            </div>
            <Link
              onClick={() => setMenuOpened(false)}
              to={accountUrl}
              target={"_blank"}
              className={classes.menuItemWrapper}
            >
              <ExplorerIcon />
              <span className={classes.menuItemText}>
                <Trans>View in Explorer</Trans>
              </span>
            </Link>
            <div
              onClick={() => {
                disconnectAccountAndCloseSettings();
                setMenuOpened(false);
              }}
              className={classes.menuItemWrapper}
            >
              <DisconnectIcon />
              <span className={classes.menuItemText}>
                <Trans>Disconnect</Trans>
              </span>
            </div>
          </motion.div>
        )}
      </div>
    </>
  );
}

export default AddressDropdown;
